<template>
  <div class="app">
    <header class="app-header">
      <div class="container">
        <h1>{{ t('app.title') }}</h1>
        <p class="subtitle">{{ t('app.subtitle') }}</p>
      </div>
    </header>

    <main class="app-main">
      <component :is="currentTabComponent" />
    </main>

    <nav class="app-nav">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['nav-button', { active: currentTab === tab.id }]"
        @click="currentTab = tab.id"
      >
        <span class="nav-icon">{{ tab.icon }}</span>
        <span class="nav-label">{{ t(tab.labelKey) }}</span>
      </button>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import OverviewView from './views/OverviewView.vue';
import MyRoleView from './views/MyRoleView.vue';
import RulesView from './views/RulesView.vue';
import InfoView from './views/InfoView.vue';

const { t } = useI18n();

interface Tab {
  id: string;
  labelKey: string;
  icon: string;
  component: any;
}

const tabs: Tab[] = [
  { id: 'overview', labelKey: 'nav.overview', icon: '📋', component: OverviewView },
  { id: 'myRole', labelKey: 'nav.myRole', icon: '👤', component: MyRoleView },
  { id: 'rules', labelKey: 'nav.rules', icon: '📖', component: RulesView },
  { id: 'info', labelKey: 'nav.info', icon: 'ℹ️', component: InfoView },
];

const currentTab = ref('overview');

const currentTabComponent = computed(() => {
  return tabs.find(tab => tab.id === currentTab.value)?.component;
});
</script>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.app-header {
  background: linear-gradient(135deg, var(--color-accent-red), var(--color-accent-red-light));
  padding: var(--spacing-lg) 0;
  box-shadow: var(--shadow-md);
}

.app-header h1 {
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-xs);
  text-align: center;
  font-size: 1.75rem;
}

.subtitle {
  color: var(--color-text-secondary);
  text-align: center;
  font-size: 1rem;
  margin: 0;
}

.app-main {
  flex: 1;
  padding: var(--spacing-lg) 0;
  padding-bottom: calc(80px + var(--spacing-lg));
  overflow-y: auto;
}

.app-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--color-bg-secondary);
  border-top: 2px solid var(--color-bg-tertiary);
  display: flex;
  justify-content: space-around;
  padding: var(--spacing-sm);
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.nav-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm);
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  min-width: 60px;
}

.nav-button:hover {
  color: var(--color-text-secondary);
}

.nav-button.active {
  color: var(--color-accent-gold);
}

.nav-button.active .nav-icon {
  transform: scale(1.2);
}

.nav-icon {
  font-size: 1.5rem;
  margin-bottom: var(--spacing-xs);
  transition: transform var(--transition-fast);
}

.nav-label {
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
}

@media (max-width: 768px) {
  .nav-label {
    font-size: 0.7rem;
  }

  .nav-icon {
    font-size: 1.25rem;
  }
}
</style>
